import { decisionFicheFournisseur, formaterJour, formaterMontant } from '@admin-pmvb/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api, corps, lire, requetes } from '../api'
import { Vide } from './Section'

// « Compléter la fiche du fournisseur » (décision du 14/10/2026, packages/core/src/fiche-fournisseur.ts) :
// une pièce venue d'un mail a été rapprochée de son opération ; on propose d'ajouter à la fiche
// de son fournisseur le libellé bancaire de l'opération et l'adresse qui a envoyé la pièce, pour
// que la recherche retrouve seule ses prochaines factures. Rien n'est écrit sans « Compléter la
// fiche » ; « Ne pas compléter » ne la propose plus.

export function DecisionFicheFournisseur({
  pieceId,
  apres,
}: {
  pieceId: string
  apres: () => void
}) {
  const client = useQueryClient()
  const piece = useQuery(requetes.piece(pieceId))
  const [erreur, setErreur] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)
  const p = piece.data
  if (!p) return <Vide>{piece.isPending ? 'Chargement…' : 'Pièce introuvable.'}</Vide>
  const f = p.ficheFournisseur
  if (!f) return <Vide>Plus rien à proposer : la fiche du fournisseur a déjà ce qu’il faut.</Vide>

  const montant = p.montants?.ttc ?? p.montants?.aPayer ?? null
  const quoi = [
    p.numero,
    p.dateDocument && formaterJour(p.dateDocument),
    montant !== null && formaterMontant(montant, p.devise),
  ]
    .filter(Boolean)
    .join(' · ')
  const trancher = async (accepter: boolean) => {
    setErreur(null)
    setEnCours(true)
    try {
      await lire(
        api.pieces[':id']['fiche-fournisseur'].$post({
          param: { id: pieceId },
          json: corps(decisionFicheFournisseur, { accepter }),
        }),
      )
      await client.invalidateQueries()
      apres()
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Action impossible.')
    } finally {
      setEnCours(false)
    }
  }

  return (
    <>
      <p>
        Pièce de <strong>{f.fournisseur}</strong>
        {quoi && <> ({quoi})</>}, rapprochée de son opération. Ajouter à sa fiche :
      </p>
      <dl className="details-grille">
        {f.libelle && (
          <>
            <dt>Libellé bancaire</dt>
            <dd>
              <code>{f.libelle}</code>
            </dd>
          </>
        )}
        {f.adresse && (
          <>
            <dt>Adresse d’envoi</dt>
            <dd>
              <code>{f.adresse}</code>
            </dd>
          </>
        )}
      </dl>
      <p className="discret">
        La recherche dans les mails trouvera ensuite ses factures par cet expéditeur et le montant.
      </p>
      <div className="boutons">
        <button
          type="button"
          className="bouton bouton-principal"
          disabled={enCours}
          onClick={() => void trancher(true)}
        >
          Compléter la fiche
        </button>
        <button
          type="button"
          className="bouton"
          disabled={enCours}
          onClick={() => void trancher(false)}
        >
          Ne pas compléter
        </button>
        <a
          href={`/api/pieces/${pieceId}/fichier`}
          target="_blank"
          rel="noreferrer"
          className="bouton-discret"
          title="Le document tel qu’il a été reçu"
        >
          Voir le PDF
        </a>
      </div>
      {erreur && <p className="alerte">{erreur}</p>}
    </>
  )
}
